import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const TermsConditions = () => {
  const accentColor = '#0369a1';
  const lastUpdated = 'July 30, 2026';

  return (
    <LegalLayout app="KinRelay" docType="Terms & Conditions" icon={FileText} lastUpdated={lastUpdated}>
      <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Agreement to Terms
              </h2>
              <p className="mb-4 leading-relaxed">
                By downloading, installing, or using KinRelay ("the App"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do not use the App.
              </p>
              <p className="leading-relaxed">
                These Terms constitute a legally binding agreement between you and Feral Techlabs ("we," "our," or "us") regarding your use of the App.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Description of Service
              </h2>
              <p className="mb-4 leading-relaxed">
                KinRelay is a family care coordination tool that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Runs a task board with explicit ownership, so who-owns-what is never ambiguous</li>
                <li>Keeps a changelog of every update — what changed, when, and by whom it was recorded</li>
                <li>Stores key documents and photos alongside the care record</li>
                <li>Exports Care Updates as text or PDF for sharing with siblings</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                License Grant
              </h2>
              <p className="mb-4 leading-relaxed">
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to download, install, and use the App for your personal, non-commercial purposes on any Apple-branded device that you own or control.
              </p>
              <p className="leading-relaxed">
                This license does not allow you to use the App on any device that you do not own or control, and you may not distribute or make the App available over a network where it could be used by multiple devices at the same time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                User Responsibilities
              </h2>
              <p className="mb-4 leading-relaxed">
                You are solely responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The accuracy of care information you record and share with family</li>
                <li>Respecting your parents’ privacy and dignity in what you record, and obtaining consent where appropriate</li>
                <li>Actual care decisions and their coordination — KinRelay organizes information; people provide the care</li>
                <li>Where you share exported Care Updates and who can see them</li>
                <li>Backing up any important data you rely on — see the Data Loss Disclaimer below</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Not a Medical or Care Service
              </h2>
              <p className="leading-relaxed">
                KinRelay is an organizational tool for family logistics. It is not a medical device, health record system, or care provider; it does not provide medical advice, and notes about medications, appointments, or conditions are informal family records, not clinical documentation. Always rely on healthcare professionals and official records for medical decisions, and seek immediate care in emergencies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Restrictions
              </h2>
              <p className="mb-4 leading-relaxed">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Copy, modify, or distribute the App or any portion thereof</li>
                <li>Reverse engineer, decompile, or disassemble the App</li>
                <li>Rent, lease, lend, sell, or sublicense the App</li>
                <li>Use the App for any unlawful purpose or in violation of applicable laws</li>
                <li>Attempt to circumvent the App's security features</li>
                <li>Remove, alter, or obscure any proprietary notices on the App</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Apple Standard EULA
              </h2>
              <p className="leading-relaxed">
                Use of the App is also governed by{' '}
                <a
                  href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors"
                  style={{ color: accentColor }}
                >
                  Apple's Standard End-User License Agreement
                </a>
                {' '}for applications licensed through the App Store, which applies in addition to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Intellectual Property
              </h2>
              <p className="leading-relaxed">
                The App, including all content, features, and functionality, is owned by Feral Techlabs and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. Our trademarks may not be used without our prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Loss Disclaimer
              </h2>
              <p className="leading-relaxed">
                While we strive to provide a reliable service, we cannot guarantee against data loss. You acknowledge that your care tasks, updates, and documents stored on your device may be lost due to device failure, iOS updates, app updates, or other unforeseen circumstances. We are not responsible for any inconvenience resulting from data loss in the App.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Disclaimer of Warranties
              </h2>
              <p className="mb-4 leading-relaxed">
                THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="leading-relaxed">
                We do not warrant that the App will be uninterrupted, error-free, or free of viruses or other harmful components. You assume all responsibility and risk for your use of the App.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Limitation of Liability
              </h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL FERAL TECHLABS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OR INABILITY TO USE THE APP, INCLUDING BUT NOT LIMITED TO CARE OUTCOMES OR DECISIONS COORDINATED THROUGH THE APP.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Indemnification
              </h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless Feral Techlabs and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with your use of the App, your violation of these Terms, or your violation of any rights of another.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Termination
              </h2>
              <p className="leading-relaxed">
                We may terminate or suspend your access to the App immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the App will immediately cease. You may also terminate this agreement at any time by uninstalling the App from your device.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to Terms
              </h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by updating the "Last updated" date at the top of these Terms. Your continued use of the App after any such changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Governing Law
              </h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Feral Techlabs operates, without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/kinrelay/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    KinRelay Support Page
                  </Link>
                </p>
              </div>
            </section>
      </div>
    </LegalLayout>
  );
};

export default TermsConditions;
